import { Schema } from "effect"
import { Millis, NonNegInt, Sha256 } from "./ids"

// #region files
/** Decision 0050. One storage: R2 binding ARTIFACTS, key `files/<sha256>`, URI `r2://artifacts/<sha256>`. */
export const FILE_MAX_BYTES = 25 * 1024 * 1024
export const FILE_URI_PREFIX = "r2://artifacts/"
export const fileUri = (sha256: string): string => `${FILE_URI_PREFIX}${sha256}`
/** The sha256 of an `r2://artifacts/<sha>` URI, else null (any other r2:// form is invalid). */
export const shaOfFileUri = (uri: string): string | null =>
  /^r2:\/\/artifacts\/([0-9a-f]{64})$/.exec(uri)?.[1] ?? null
export const isFileUri = (uri: string): boolean => uri.startsWith("r2://")

/** The only content types Minga stores (415 otherwise). `preview`: how the web shows it; `ext`: the file name suffix. */
export const FILE_TYPES = {
  "image/png": { ext: "png", preview: "image" },
  "image/jpeg": { ext: "jpg", preview: "image" },
  "image/gif": { ext: "gif", preview: "image" },
  "image/webp": { ext: "webp", preview: "image" },
  "image/svg+xml": { ext: "svg", preview: "image" },
  "application/pdf": { ext: "pdf", preview: "link" },
  "text/html": { ext: "html", preview: "link" },
  "video/mp4": { ext: "mp4", preview: "video" },
  "video/webm": { ext: "webm", preview: "video" },
  "video/quicktime": { ext: "mov", preview: "video" },
  "text/markdown": { ext: "md", preview: "markdown" },
  "text/plain": { ext: "txt", preview: "text" },
  "text/csv": { ext: "csv", preview: "text" },
  "application/json": { ext: "json", preview: "text" },
  // decision 0171: a folder of files in one upload; agents unzip it in their workspace, the app only downloads it
  "application/zip": { ext: "zip", preview: "file" },
  // wave DOC (decision 0068): support files of HTML documents. Never executed on the app origin (F5 serves them as
  // attachments); only the user-content Worker serves them with their real type
  "text/javascript": { ext: "js", preview: "file" },
  "text/css": { ext: "css", preview: "file" },
  "font/woff2": { ext: "woff2", preview: "file" },
  "font/woff": { ext: "woff", preview: "file" },
  "font/ttf": { ext: "ttf", preview: "file" },
  "font/otf": { ext: "otf", preview: "file" },
} as const
export type FileContentType = keyof typeof FILE_TYPES
export const isFileContentType = (t: string): t is FileContentType => Object.hasOwn(FILE_TYPES, t)

/** A Content-Type header value without parameters (`text/csv; charset=utf-8` → text/csv), lowercased; else null. */
export const fileContentTypeOf = (header: string): FileContentType | null => {
  const bare = (header.split(";")[0] ?? "").trim().toLowerCase()
  return isFileContentType(bare) ? bare : null
}

/** Decision 0094: text sources (code, config, logs) Minga stores as `text/plain`. */
export const TEXT_SOURCE_EXTS: ReadonlyArray<string> = [
  "rb",
  "py",
  "sh",
  "bash",
  "zsh",
  "fish",
  "ts",
  "tsx",
  "jsx",
  "cjs",
  "mts",
  "cts",
  "sql",
  "go",
  "swift",
  "rs",
  "yaml",
  "yml",
  "toml",
  "ini",
  "cfg",
  "conf",
  "env",
  "kt",
  "kts",
  "java",
  "scala",
  "c",
  "h",
  "cc",
  "cpp",
  "hpp",
  "m",
  "mm",
  "cs",
  "php",
  "pl",
  "lua",
  "r",
  "ex",
  "exs",
  "erl",
  "hs",
  "clj",
  "dart",
  "vue",
  "svelte",
  "astro",
  "graphql",
  "gql",
  "proto",
  "tf",
  "hcl",
  "xml",
  "diff",
  "patch",
  "log",
  "tsv",
  "rst",
  "adoc",
  "tex",
  "gradle",
  "lock",
  "dockerfile",
  "makefile",
  "mdx",
]

/**
 * Decision 0094: a Content-Type header for text Minga does not list (`text/x-ruby`, `application/x-sh`,
 * `application/yaml`) is stored as `text/plain`; a listed type is itself; anything else null.
 */
export const textContentTypeOf = (header: string): FileContentType | null => {
  const listed = fileContentTypeOf(header)
  if (listed !== null) return listed
  const bare = (header.split(";")[0] ?? "").trim().toLowerCase()
  if (bare.startsWith("text/")) return "text/plain"
  if (
    /^application\/(x-)?(sh|shellscript|yaml|toml|sql|typescript|javascript|ruby|python|xml|php|graphql)$/.test(
      bare,
    )
  )
    return "text/plain"
  return null
}

const EXTRA_EXTS: Readonly<Record<string, FileContentType>> = {
  jpeg: "image/jpeg",
  htm: "text/html",
  markdown: "text/markdown",
  mjs: "text/javascript",
  // decision 0094: source and config files are stored (and shown) as plain text
  ...Object.fromEntries(TEXT_SOURCE_EXTS.map((e) => [e, "text/plain" as const])),
}
/** By file extension (runner, web). Lowercase, without the dot; `jpeg` → image/jpeg, `htm` → text/html. */
export const contentTypeOfExt = (ext: string): FileContentType | null => {
  const e = ext.replace(/^\./, "").toLowerCase()
  if (e === "") return null
  const extra = EXTRA_EXTS[e]
  if (extra !== undefined) return extra
  for (const [type, info] of Object.entries(FILE_TYPES)) if (info.ext === e) return type as FileContentType
  return null
}

/** Images a model provider takes as an image part (Anthropic: these four, ≤ 5 MB). Everything else is a text line. */
export const MODEL_IMAGE_TYPES: ReadonlySet<FileContentType> = new Set<FileContentType>([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
])
export const MODEL_IMAGE_MAX_BYTES = 5 * 1024 * 1024

/** Request headers of both upload routes. */
export const UPLOAD_HEADERS = { filename: "x-minga-filename", sha256: "x-minga-sha256" } as const
/** Person upload: `?source=item` (default), `?source=chat` or `?source=doc` (wave DOC); `run` for a run's upload. */
export const FileSource = Schema.Literals(["item", "chat", "run", "doc"])
export type FileSource = typeof FileSource.Type

/** F1 / F2 response. `deduped`: the bytes were already stored for this project. */
export const FileUploadResult = Schema.Struct({
  sha256: Sha256,
  sizeBytes: NonNegInt,
  contentType: Schema.String,
  uri: Schema.String, // fileUri(sha256)
  deduped: Schema.Boolean,
})
export type FileUploadResult = typeof FileUploadResult.Type
/** F4 request and response. Unknown or foreign hashes are left out of `urls`. */
export const FileSignBody = Schema.Struct({
  sha256: Schema.Array(Sha256).check(Schema.isMinLength(1), Schema.isMaxLength(50)),
})
export type FileSignBody = typeof FileSignBody.Type
export const SignedFileUrl = Schema.Struct({ url: Schema.String, expiresAt: Millis })
export type SignedFileUrl = typeof SignedFileUrl.Type
export const FileSignResult = Schema.Struct({ urls: Schema.Record(Schema.String, SignedFileUrl) })
export type FileSignResult = typeof FileSignResult.Type
// #endregion files
